/**
 * CareStart Studio — facility variant CRUD (Phase 2).
 *
 * WHAT: Direct Firestore access for `careStartVariants/{variantSlug}` (slug = document ID).
 * WHY: Supervisors create named CareStart deployments without Cloud Functions; public pages
 *   fetch active variants by slug with rules-gated single-doc reads.
 * INVARIANT: `facilityId` is always `userData.location` (a `locations/{id}` ID), never a
 *   `clinics/{id}`. Check-in maps clinic → location at read time. Supervisors deactivate
 *   (`active: false`); they never delete docs (admin-only per rules).
 * Coupling: CareStartStudioPage, CareStartVariantPage, PatientCheckInPage screening offer.
 */

import { getApp } from 'firebase/app';
import { isCareStartCitySlug } from '../../pages/pages/scmap4/careStartCityCatalog';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  limit,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';

const COLLECTION = 'careStartVariants';
const SLUG_REGEX = /^[a-z0-9-]{3,30}$/;
const SLUG_TAKEN_MESSAGE = 'That URL is already taken — try a different name.';

const db = getFirestore(getApp());

/**
 * Map Firebase errors to plain English for Clinic Managers.
 * @param {unknown} error
 * @param {string} fallback
 * @returns {Error}
 */
function toUserFriendlyError(error, fallback) {
  const code = error?.code || '';
  if (code === 'permission-denied') {
    return new Error('You do not have permission to change this CareStart variant.');
  }
  if (code === 'unavailable' || code === 'deadline-exceeded') {
    return new Error('Could not reach the server. Check your connection and try again.');
  }
  console.warn('[careStartVariantService]', fallback, error);
  return new Error(fallback);
}

/**
 * Build a URL-safe slug from a display name (supervisor may edit before save).
 * @param {string} displayName
 * @returns {string}
 */
export function generateSlugFromDisplayName(displayName) {
  const raw = String(displayName || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-{2,}/g, '-');
  const trimmed = raw.slice(0, 30).replace(/-+$/g, '');
  return trimmed.length >= 3 ? trimmed : '';
}

/**
 * @param {string} slug
 * @returns {boolean}
 */
export function isValidVariantSlug(slug) {
  return SLUG_REGEX.test(String(slug || ''));
}

/**
 * Create a new active CareStart variant. Document ID = variantSlug.
 *
 * @param {object} variantData — displayName, variantSlug, aimKey, aimCustomText, audienceMode, checkInEnabled
 * @param {{ uid: string, location?: string }} userData — authenticated supervisor; location required
 * @returns {Promise<string>} variantSlug
 */
export async function createCareStartVariant(variantData, userData) {
  const facilityId = userData?.location || '';
  if (!facilityId) {
    throw new Error(
      'Your account must be linked to a facility before you can create a CareStart variant.'
    );
  }
  if (!userData?.uid) {
    throw new Error('You must be signed in to create a CareStart variant.');
  }

  const variantSlug = String(variantData?.variantSlug || '').trim();
  if (!isValidVariantSlug(variantSlug) || variantSlug === 'help' || isCareStartCitySlug(variantSlug)) {
    throw new Error(
      variantSlug === 'help' || isCareStartCitySlug(variantSlug)
        ? SLUG_TAKEN_MESSAGE
        : 'Your link name must be 3–30 characters and use only lowercase letters, numbers, and hyphens.'
    );
  }

  const displayName = String(variantData?.displayName || '').trim();
  if (!displayName) {
    throw new Error('Please enter a name for this CareStart variant.');
  }

  const aimKey = variantData?.aimKey || 'patient_self_triage';
  const aimCustomText =
    aimKey === 'custom' ? String(variantData?.aimCustomText || '').trim() || null : null;
  if (aimKey === 'custom' && !aimCustomText) {
    throw new Error('Please describe what this conversation is for.');
  }

  const audienceMode =
    variantData?.audienceMode === 'staff' ? 'staff' : 'patient';
  const checkInEnabled =
    audienceMode === 'patient' && Boolean(variantData?.checkInEnabled);

  try {
    const slugRef = doc(db, COLLECTION, variantSlug);
    const existing = await getDoc(slugRef);
    if (existing.exists()) {
      throw new Error(SLUG_TAKEN_MESSAGE);
    }

    await setDoc(slugRef, {
      variantSlug,
      displayName,
      facilityId,
      createdBy: userData.uid,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      active: true,
      aimKey,
      aimCustomText,
      audienceMode,
      checkInEnabled,
      promptOverrides: {},
    });

    return variantSlug;
  } catch (error) {
    if (error?.message === SLUG_TAKEN_MESSAGE) throw error;
    throw toUserFriendlyError(error, 'Could not create your CareStart variant. Please try again.');
  }
}

/**
 * Update an existing variant (supervisor-owned fields only).
 *
 * @param {string} variantSlug
 * @param {object} updates
 * @param {{ location?: string }} userData
 * @returns {Promise<void>}
 */
export async function updateCareStartVariant(variantSlug, updates, userData) {
  const facilityId = userData?.location || '';
  if (!facilityId || !variantSlug) {
    throw new Error('Missing facility or variant information.');
  }

  const patch = { updatedAt: serverTimestamp() };
  if (updates.displayName !== undefined) {
    patch.displayName = String(updates.displayName || '').trim();
  }
  if (updates.aimKey !== undefined) {
    patch.aimKey = updates.aimKey;
  }
  if (updates.aimCustomText !== undefined) {
    patch.aimCustomText = updates.aimCustomText;
  }
  if (updates.audienceMode !== undefined) {
    patch.audienceMode = updates.audienceMode === 'staff' ? 'staff' : 'patient';
    if (patch.audienceMode === 'staff') {
      patch.checkInEnabled = false;
    }
  }
  if (updates.checkInEnabled !== undefined && patch.audienceMode !== 'staff') {
    patch.checkInEnabled = Boolean(updates.checkInEnabled);
  }
  if (updates.active !== undefined) {
    patch.active = Boolean(updates.active);
  }

  try {
    await updateDoc(doc(db, COLLECTION, variantSlug), patch);
  } catch (error) {
    throw toUserFriendlyError(error, 'Could not update this CareStart variant. Please try again.');
  }
}

/**
 * List variants for a supervisor facility (location ID), newest first.
 *
 * @param {string} facilityId — locations doc ID
 * @returns {Promise<Array<object>>}
 */
export async function getCareStartVariantsForFacility(facilityId) {
  if (!facilityId) return [];

  try {
    const q = query(
      collection(db, COLLECTION),
      where('facilityId', '==', facilityId),
      orderBy('createdAt', 'desc')
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (error) {
    console.warn('[careStartVariantService] getCareStartVariantsForFacility failed', error);
    throw toUserFriendlyError(error, 'Could not load your CareStart variants.');
  }
}

/**
 * Fetch one variant by slug (public + supervisor). Returns null if missing or unreadable.
 *
 * @param {string} variantSlug
 * @returns {Promise<object|null>}
 */
export async function getCareStartVariantBySlug(variantSlug) {
  if (!variantSlug) return null;

  try {
    const snap = await getDoc(doc(db, COLLECTION, variantSlug));
    if (!snap.exists()) return null;
    return { id: snap.id, ...snap.data() };
  } catch (error) {
    console.warn('[careStartVariantService] getCareStartVariantBySlug failed', error);
    return null;
  }
}

/**
 * Find one active check-in-enabled variant for a facility (location ID).
 * Used by PatientCheckInPage screening offer (read-only).
 *
 * @param {string} facilityId — locations doc ID from clinic.locationId
 * @returns {Promise<object|null>}
 */
export async function getCheckInEnabledVariantForFacility(facilityId) {
  if (!facilityId) return null;

  try {
    const q = query(
      collection(db, COLLECTION),
      where('facilityId', '==', facilityId),
      where('checkInEnabled', '==', true),
      where('active', '==', true),
      limit(1)
    );
    const snap = await getDocs(q);
    if (snap.empty) return null;
    const d = snap.docs[0];
    return { id: d.id, ...d.data() };
  } catch (error) {
    console.warn('[careStartVariantService] getCheckInEnabledVariantForFacility failed', error);
    return null;
  }
}

/**
 * Saves prompt overrides for a variant. Only saves non-empty overrides;
 * cleared fields (empty string) are removed from promptOverrides (not stored as "").
 *
 * @param {string} variantSlug
 * @param {Record<string, string>} overrides — { [queryId]: string }; empty strings clear that key
 * @param {{ location?: string }} userData
 * @returns {Promise<void>}
 */
export async function saveVariantPromptOverrides(variantSlug, overrides, userData) {
  const facilityId = userData?.location || '';
  if (!facilityId || !variantSlug) {
    throw new Error('Missing facility or variant information.');
  }
  if (!isValidVariantSlug(variantSlug)) {
    throw new Error('Invalid variant link name.');
  }

  try {
    const variantRef = doc(db, COLLECTION, variantSlug);
    const variantSnap = await getDoc(variantRef);
    if (!variantSnap.exists()) {
      throw new Error('This CareStart variant could not be found.');
    }
    const variantData = variantSnap.data();
    if (variantData.facilityId !== facilityId) {
      throw new Error('You do not have permission to change prompts for this variant.');
    }

    const existing = variantData.promptOverrides && typeof variantData.promptOverrides === 'object'
      ? { ...variantData.promptOverrides }
      : {};
    const patchOverrides = { ...existing };

    for (const [queryId, rawValue] of Object.entries(overrides || {})) {
      const trimmed = typeof rawValue === 'string' ? rawValue.trim() : '';
      if (trimmed.length > 0) {
        patchOverrides[queryId] = trimmed;
      } else {
        delete patchOverrides[queryId];
      }
    }

    await updateDoc(variantRef, {
      promptOverrides: patchOverrides,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    if (error?.message?.includes('permission') || error?.message?.includes('variant')) {
      throw error;
    }
    throw toUserFriendlyError(error, 'Could not save prompt changes. Please try again.');
  }
}

const careStartVariantService = {
  generateSlugFromDisplayName,
  isValidVariantSlug,
  createCareStartVariant,
  updateCareStartVariant,
  getCareStartVariantsForFacility,
  getCareStartVariantBySlug,
  getCheckInEnabledVariantForFacility,
  saveVariantPromptOverrides,
};

export default careStartVariantService;
